if (!customElements.get("cart-drawer-subtotal")) {
  customElements.define(
    "cart-drawer-subtotal",
    class CartDrawerSubtotal extends HTMLElement {
      value: number;
      totalPriceFromCart: number;
      pixAsOriginalPrice: boolean;
      hasPixDiscountInCart: boolean;
      pixDiscountValue: number;
      promotionCouponReadyHandler: () => void;

      static get observedAttributes() {
        return ["data-value"];
      }

      constructor() {
        super();
        this.value = this.dataset.value ? parseInt(this.dataset.value) : 0;
        this.pixAsOriginalPrice = this.dataset.pixAsOriginalPrice === "true";
        this.pixDiscountValue = this.dataset.pixDiscountValue
          ? parseFloat(this.dataset.pixDiscountValue)
          : 0;
        this.hasPixDiscountInCart = this.dataset.hasPixDiscountInCart === "true";
        this.totalPriceFromCart = this.dataset.totalPrice
          ? parseInt(this.dataset.totalPrice)
          : 0;

        this.promotionCouponReadyHandler = () => {
          // price-normalize também reage a PromotionCouponReady. Esperamos dois frames
          // para consumir o priceObject já atualizado, sem depender de ordem de listeners.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.init());
          });
        };
      }

      attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (name !== "data-value" || oldValue === newValue) return;
        const next = newValue ? parseInt(newValue) : 0;
        this.value = Number.isNaN(next) ? 0 : next;
        this.init();
      }

      connectedCallback(): void {
        window.addEventListener("PromotionCouponReady", this.promotionCouponReadyHandler);
        setTimeout(() => this.init(), 2000);
      }

      disconnectedCallback(): void {
        window.removeEventListener("PromotionCouponReady", this.promotionCouponReadyHandler);
      }

      normalizeCode(value: unknown) {
        return String(value || "").trim().toUpperCase();
      }

      toDisplayReais(cents: number) {
        const normalized = Math.round(Number(cents) * 1000000) / 1000000;
        return Math.max(0, Math.ceil(normalized / 100));
      }

      formatDisplayReais(value: number) {
        return `R$ ${Math.max(0, value).toLocaleString("pt-BR")}`;
      }

      parseDisplayMoneyToCents(value: string | undefined) {
        if (!value) return NaN;
        const numeric = Number(
          value
            .replace("R$", "")
            .replace(/\s/g, "")
            .replace(/\./g, "")
            .replace(",", "."),
        );
        return Number.isFinite(numeric) ? Math.round(numeric * 100) : NaN;
      }

      createSummaryRow(
        label: string,
        value: number,
        options: {
          negative?: boolean;
          variant?: "subtotal" | "discount" | "coupon" | "pix" | "total";
        } = {},
      ) {
        const row = document.createElement("div");
        row.className = "cart-summary__row";

        if (options.variant) {
          row.classList.add(`cart-summary__row--${options.variant}`);
        }

        const labelEl = document.createElement("span");
        labelEl.className = "cart-summary__label";

        if (options.variant === "coupon") {
          const couponCode = label.replace(/^CUPOM\s+/i, "").trim();

          const prefixEl = document.createElement("span");
          prefixEl.className = "cart-summary__coupon-prefix";
          prefixEl.textContent = "CUPOM APLICADO ";

          const codeEl = document.createElement("span");
          codeEl.className = "cart-summary__coupon-code";
          codeEl.textContent = `(${couponCode})`;

          labelEl.append(prefixEl, codeEl);
        } else {
          labelEl.textContent = label;
        }

        const valueEl = document.createElement("span");
        valueEl.className = "cart-summary__value";
        valueEl.textContent =
          `${options.negative ? "- " : ""}${this.formatDisplayReais(value)}`;

        row.append(labelEl, valueEl);
        return row;
      }

      readAllocations(item: HTMLElement) {
        const raw = item.dataset.couponAllocations;
        if (!raw) return [] as Array<{ code: string; amount: number }>;

        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
          return parsed
            .map((allocation) => ({
              code: this.normalizeCode(allocation?.code),
              amount: Number(allocation?.amount),
            }))
            .filter(
              (allocation) =>
                allocation.code &&
                Number.isFinite(allocation.amount) &&
                allocation.amount > 0,
            );
        } catch (error) {
          console.error("[cart-drawer-subtotal] coupon allocations invalidas:", error);
          return [];
        }
      }

      init() {
        this.innerHTML = "";

        let referenceSubtotalCents = 0;
        let commercialSubtotalCents = 0;
        let finalSubtotalCents = 0;
        let hasReferenceLinePrice = false;
        let hasCommercialLinePrice = false;
        let hasFinalLinePrice = false;
        let previewDiscountCodeCents = 0;

        const couponTotals = new Map<string, number>();
        const countedAllocationLineKeys = new Set<string>();
        const pixCode = this.normalizeCode(this.dataset.pixDiscountCode);
        let pixCodeCents = 0;

        document.querySelectorAll<HTMLElement>("#CartDrawer .cart-item").forEach((item) => {
          const referenceLinePrice = Number(item.dataset.referenceLinePrice);
          const commercialLinePrice = Number(item.dataset.commercialLinePrice);
          const finalLinePrice = Number(item.dataset.finalLinePrice);

          if (Number.isFinite(referenceLinePrice) && referenceLinePrice >= 0) {
            referenceSubtotalCents += referenceLinePrice;
            hasReferenceLinePrice = true;
          }

          if (Number.isFinite(commercialLinePrice) && commercialLinePrice >= 0) {
            commercialSubtotalCents += commercialLinePrice;
            hasCommercialLinePrice = true;
          }

          if (Number.isFinite(finalLinePrice) && finalLinePrice >= 0) {
            finalSubtotalCents += finalLinePrice;
            hasFinalLinePrice = true;
          }

          const actualAllocations = this.readAllocations(item);
          const actualCodes = new Set<string>();
          const cartKey = item.dataset.cartKey || "";
          const allocationAlreadyCounted = Boolean(
            cartKey && countedAllocationLineKeys.has(cartKey),
          );
          if (cartKey) countedAllocationLineKeys.add(cartKey);

          actualAllocations.forEach(({ code, amount }) => {
            actualCodes.add(code);
            if (allocationAlreadyCounted) return;
            if (pixCode && code === pixCode) {
              pixCodeCents += amount;
              return;
            }
            couponTotals.set(code, (couponTotals.get(code) || 0) + amount);
          });

          // Quando o cupom ainda é apenas a projeção usada pelo site para o checkout,
          // não há discount_allocation no cart. Reutilizamos a decisão já resolvida pelo
          // price-normalize para não duplicar regras de elegibilidade no subtotal.
          const priceNormalize = item.querySelector<HTMLElement>(
            "price-normalize[data-placement='cartDrawer']",
          );
          const priceObjectType = priceNormalize?.dataset.priceObjectType || "";
          const previewCode = this.normalizeCode(
            priceNormalize?.dataset.priceObjectDiscountCode,
          );
          const previewBaseCents = this.parseDisplayMoneyToCents(
            priceNormalize?.dataset.priceObjectPrice,
          );
          const previewDiscountedCents = this.parseDisplayMoneyToCents(
            priceNormalize?.dataset.priceObjectPriceWithDiscountCode,
          );

          if (
            priceObjectType.includes("DiscountCode") &&
            previewCode &&
            !actualCodes.has(previewCode) &&
            Number.isFinite(previewBaseCents) &&
            Number.isFinite(previewDiscountedCents) &&
            previewBaseCents > previewDiscountedCents
          ) {
            // Usa exatamente a etapa de preço já resolvida pelo price-normalize.
            // Isso evita duplicar aqui regras de kit, elegibilidade e finalPriceApplied.
            const previewAmount = previewBaseCents - previewDiscountedCents;

            if (pixCode && previewCode === pixCode) {
              pixCodeCents += previewAmount;
            } else {
              couponTotals.set(
                previewCode,
                (couponTotals.get(previewCode) || 0) + previewAmount,
              );
            }
            previewDiscountCodeCents += previewAmount;
          }
        });

        if (!hasReferenceLinePrice || referenceSubtotalCents <= 0) {
          referenceSubtotalCents = this.value;
        }
        if (!hasFinalLinePrice) finalSubtotalCents = this.value;
        if (!hasCommercialLinePrice) commercialSubtotalCents = finalSubtotalCents;

        let effectiveFinalCents = Math.max(
          0,
          finalSubtotalCents - previewDiscountCodeCents,
        );

        if (this.hasPixDiscountInCart && this.totalPriceFromCart > 0) {
          effectiveFinalCents = Math.min(effectiveFinalCents, this.totalPriceFromCart);
        }

        const couponCents = Array.from(couponTotals.values()).reduce(
          (sum, amount) => sum + amount,
          0,
        );

        // Se o Pix for cart-level e não vier alocado nas lines, fecha a diferença aqui.
        if (this.hasPixDiscountInCart) {
          const afterKnownCodes = Math.max(
            0,
            commercialSubtotalCents - couponCents - pixCodeCents,
          );
          if (afterKnownCodes > effectiveFinalCents) {
            pixCodeCents += afterKnownCodes - effectiveFinalCents;
          }
        }

        const commercialDiscountCents = Math.max(
          0,
          referenceSubtotalCents - commercialSubtotalCents,
        );

        const subtotalDisplay = this.toDisplayReais(referenceSubtotalCents);
        const totalDisplay = this.toDisplayReais(effectiveFinalCents);
        const couponRows = Array.from(couponTotals.entries())
          .filter(([, amount]) => amount > 0)
          .map(([code, amount]) => ({
            code,
            value: this.toDisplayReais(amount),
          }));
        const couponDisplayTotal = couponRows.reduce(
          (sum, coupon) => sum + coupon.value,
          0,
        );
        const hasCouponBreakdown = couponRows.length > 0;
        const pixDisplay = this.toDisplayReais(pixCodeCents);

        // A linha de desconto absorve apenas diferenças de arredondamento de apresentação,
        // mantendo a equação visual Subtotal - Desconto - Cupom - Pix = Total.
        const discountDisplay = commercialDiscountCents > 0
          ? Math.max(
              0,
              subtotalDisplay - couponDisplayTotal - pixDisplay - totalDisplay,
            )
          : 0;

        this.dataset.priceWithDiscountCode = String(totalDisplay);

        this.append(
          this.createSummaryRow("SUBTOTAL", subtotalDisplay, {
            variant: "subtotal",
          }),
        );

        if (hasCouponBreakdown && discountDisplay > 0) {
          this.append(
            this.createSummaryRow("DESCONTO", discountDisplay, {
              negative: true,
              variant: "discount",
            }),
          );
        }

        couponRows.forEach(({ code, value }) => {
          this.append(
            this.createSummaryRow(`CUPOM ${code}`, value, {
              negative: true,
              variant: "coupon",
            }),
          );
        });

        if (pixDisplay > 0) {
          this.append(
            this.createSummaryRow("DESCONTO NO PIX", pixDisplay, {
              negative: true,
              variant: "pix",
            }),
          );
        }

        const totalRow = this.createSummaryRow("TOTAL", totalDisplay, {
          variant: "total",
        });

        if (this.pixAsOriginalPrice) {
          const label = totalRow.querySelector<HTMLElement>(
            ".cart-summary__label",
          );

          if (label) {
            label.textContent = "TOTAL NO PIX";
          }
        }

        this.append(totalRow);
      }
    },
  );
}
